import { useEffect, useRef, useState } from "react";
import useRazorpay from "react-razorpay";
import { useAuth } from "../../context/Auth";
import PaymentForm from "./payment-form";
import RazorpayBillingForm, {
  getInitialBilling,
  normalizeBilling,
  validateBilling,
} from "./razorpay-billing-form";
import { API, endpoints } from "../../api";
import { Error, LoaderSmall } from "../../components";
import { useNavigate, useParams } from "react-router-dom";
import strings, { toastType } from "../../utils/strings";
import { currencySign } from "../../utils/utils";

let timer;
const formatAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "";
  const value = Number(amount);
  return isNaN(value) ? amount : value.toLocaleString("en-IN");
};

const Subscription = () => {
  const [termsError, setTermsError] = useState(false);
  const {
    subscriptionData,
    userCheck,
    user,
    checkSubscription,
    showToast,
    updateUser,
  } = useAuth();
  const [termsChecked, setTermChecked] = useState(false);
  const [razorpayLoading, setRazorpayLoading] = useState(false);
  const [Razorpay] = useRazorpay();
  const agreementRef = useRef(null);
  const billingRef = useRef(null);
  const [billing, setBilling] = useState(() => getInitialBilling(user?.user));
  const [billingSubmitted, setBillingSubmitted] = useState(false);
  const billingErrors = validateBilling(billing);

  const params = useParams();
  const isUpdateCard = params?.type === 'new-card'

  const [waitingForWebhook, setWaitingForWebhook] = useState(false);
  const navigate = useNavigate();


  const goToSuccessPage = async (feedId) => {
    if (isUpdateCard) {
      return
    }
    try {
      const data = await checkSubscription();
      if (data?.oldSubscribed === true) {
        setWaitingForWebhook(true)
        timer = setInterval(() => goToSuccessPage(feedId), 1000)
        return
      }
      timer && clearInterval(timer)
      setWaitingForWebhook(false)
      navigate(
        `/membership-payment-success/${data?.membershipNumber}/${data?.expiryDate}/${feedId}`
      );
    } catch (e) {
      // setLoading(false);
    }
  }

  useEffect(() => {
    return () => timer && clearInterval(timer)
  }, [])

  // The profile can arrive after the first render, fill in whatever the
  // member has not typed yet.
  useEffect(() => {
    if (!user?.user) return;
    const fromProfile = getInitialBilling(user.user);
    setBilling((current) =>
      Object.keys(fromProfile).reduce(
        (next, key) => ({ ...next, [key]: current[key] || fromProfile[key] }),
        {}
      )
    );
  }, [user?.user]);

  // The phone input hands back several fields at once, the text inputs a
  // single key and value.
  const handleBillingChange = (key, value) => {
    const patch = typeof key === "object" ? key : { [key]: value };
    setBilling((current) => ({ ...current, ...patch }));
  };


  const handleTermsCheck = (e) => {
    setTermChecked(e.target.checked);
    if (e.target.checked) {
      setTermsError(false);
    }
  };

  const requireTermsAgreement = () => {
    setTermsError(true);
    agreementRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const handleRazorpaySubscription = async () => {
    if (razorpayLoading) {
      return;
    }

    if (!termsChecked && !isUpdateCard) {
      requireTermsAgreement();
      return;
    }

    if (Object.keys(billingErrors).length) {
      setBillingSubmitted(true);
      billingRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    const customer = normalizeBilling(billing);

    setRazorpayLoading(true);

    try {
      // The order endpoint builds the Razorpay customer and the order's
      // customer_details.shipping_address from the saved profile, so the
      // billing details have to be stored before the order is created.
      const profileData = new FormData();
      profileData.append("name", customer.name);
      profileData.append("mobile_number", customer.mobile_number);
      profileData.append("mobile_code", customer.mobile_code);
      profileData.append("mobile_country_code", customer.mobile_country_code);
      profileData.append("postal_address1", customer.line1);
      profileData.append("postal_address2", customer.line2);
      profileData.append("suburb_town", customer.city);
      profileData.append("state", customer.state);
      profileData.append("postcode", customer.zipcode);

      const profileRes = await API.post({
        route: endpoints.UPDATE_PROFILE,
        data: profileData,
      });

      if (profileRes?.data) {
        updateUser({ ...user, user: profileRes.data });
      }

      const createRoute = `${endpoints.RAZORPAY_SUBSCRIPTION}?is_recurring=0`;
      const data = await API.get({ route: createRoute });

      // The endpoint answers with { success, data } while older ICN endpoints
      // answer with { status, result }, so read either shape.
      const isSuccess = data?.success ?? data?.status;
      const result = data?.data || data?.result || {};
      const order = result?.order?.response || {};

      if (!isSuccess) {
        setRazorpayLoading(false);
        showToast({
          text: data?.message || "Unable to start Razorpay payment",
          type: toastType.ERROR,
        });
        return;
      }

      const orderId = result?.order_id || order?.id;
      if (!orderId) {
        setRazorpayLoading(false);
        showToast({
          text: "Unable to start Razorpay payment",
          type: toastType.ERROR,
        });
        return;
      }

      // Mandatory for the import flow: every payment carries its own invoice
      // number and a description of what is being bought.
      const invoiceNumber =
        result?.invoice_number ||
        order?.receipt ||
        `ICN-${user?.user?.id || "M"}-${Date.now()}`;
      const goodsDescription =
        result?.goods_description || "ICN Premium Membership (12 months)";

      // The order endpoint does not echo the amount, fall back to the plan so
      // checkout never opens with an empty total.
      const planAmount = Number(subscriptionData?.plan?.amount);
      const amount =
        result?.amount ||
        order?.amount ||
        (planAmount ? Math.round(planAmount * 100) : undefined);

      const options = {
        key:
          process.env.REACT_APP_RAZORPAY_KEY_MEMBERSHIP ||
          process.env.REACT_APP_RAZORPAY_KEY,
        order_id: orderId,
        ...(amount ? { amount } : {}),
        currency: result?.currency || order?.currency || "INR",
        // Links the payment to the Razorpay customer created server side,
        // required for the import flow.
        ...(result?.razorpay_customer_id || result?.customer_id
          ? { customer_id: result?.razorpay_customer_id || result?.customer_id }
          : {}),
        name: "ICN",
        description: "Premium Membership",
        handler: async function (response) {
          try {
            const verifyData = new FormData();
            verifyData.append("razorPaymentId", response?.razorpay_payment_id || "");
            verifyData.append("razorOrderId", response?.razorpay_order_id || "");
            verifyData.append("razorSignature", response?.razorpay_signature || "");
            verifyData.append("is_recurring", 0);
            verifyData.append("invoice_number", invoiceNumber);

            const verifyRes = await API.post({
              route: endpoints.RAZORPAY_SUBSCRIPTION,
              data: verifyData,
            });

            if (!(verifyRes?.success ?? verifyRes?.status)) {
              setRazorpayLoading(false);
              showToast({
                text: verifyRes?.message || "Payment verification failed",
                type: toastType.ERROR,
              });
              return;
            }

            const verifyResult = verifyRes?.data || verifyRes?.result || {};
            const feedId = verifyResult?.feed_id || verifyResult?.feedId;
            goToSuccessPage(feedId);
          } catch (verifyError) {
            setRazorpayLoading(false);
            showToast({
              text:
                verifyError?.response?.data?.message ||
                "Payment verification failed",
              type: toastType.ERROR,
            });
          }
        },
        prefill: {
          name: customer.name,
          email: customer.email,
          contact: customer.contact,
        },
        notes: {
          invoice_number: invoiceNumber,
          goods_description: goodsDescription,
          user_id: result?.user_id || user?.user?.id,
          type: result?.type || "subscription",
        },
        method: {
          netbanking: true,
          card: true,
          upi: true,
          wallet: false,
          emi: false,
          paylater: false,
        },
        theme: {
          color: "#ef147f",
        },
        modal: {
          ondismiss: () => {
            setRazorpayLoading(false);
          },
        },
      };

      const razorpayInstance = new Razorpay(options);

      razorpayInstance.on("payment.failed", function (response) {
        setRazorpayLoading(false);
        showToast({
          text:
            response?.error?.reason ||
            response?.error?.description ||
            "Payment failed",
          type: toastType.ERROR,
        });
      });

      razorpayInstance.open();
    } catch (error) {
      setRazorpayLoading(false);
      showToast({
        text:
          error?.response?.data?.message ||
          "Unable to start Razorpay payment",
        type: toastType.ERROR,
      });
    }
  };

  useEffect(() => {
    userCheck(() => { })
  }, [userCheck])

  if (!user) {
    return <Error error="Login is required" />
  }

  if (!user?.user?.country || !user?.user?.country_code) {
    return <div className="mt-5">
      <Error error={strings.noCountry} />
    </div>
  }

  if (waitingForWebhook) {
    return <div className="card font-xsss shadow-md w-100 border-0">
      <div className="card-body align-items-center p-5">
        <LoaderSmall />
        <p>
          Thank you for your subscription purchase! We're processing your request and awaiting a response from our system. Please refrain from refreshing the page, pressing back, or making a duplicate payment. Your patience is appreciated!
        </p>
      </div>
    </div>
  }

  const isIndia = user?.user?.country_code === "IN";


  return (
    <>
      {isUpdateCard ? null : <div className="card font-xsss shadow-xss w-100 border-0 m-0 p-4 mb-3 pt-0 pb-1">
        <div className="card-body align-items-center p-0">
          <div className="agreement">
            <div className="agreement-title">
              <span className="agreement-title__icon">
                <i className="fa-sharp fa-solid fa-file-contract"></i>
              </span>
              <h4 translate="ENTER_EVENT_DRUG_TITLE">Terms &amp; Conditions</h4>
              <p translate="PROFILE_TERMS_SUBTITLE" className="ng-scope">
                In joining as a Premium Member, I hereby acknowledge and agree
                as follows:
              </p>
            </div>

            <ol className="agreement-list text-left">
              <li translate="PROFILE_TERMS1" className="ng-scope">
                I have been drug-free for a minimum of 5 years.
              </li>
              <li translate="PROFILE_TERMS2" className="ng-scope">
                Agree to be drug tested at any time and any number of times. A
                refusal to supply a sample is treated as a positive test.
              </li>
              <li translate="PROFILE_TERMS3" className="ng-scope">
                iCompete Natural (ICN), National and International Federations
                have jurisdiction to impose sanctions.
              </li>
              <li translate="PROFILE_TERMS4" className="ng-scope">
                Decisions of iCompete Natural (ICN) shall be final and
                enforceable, and that I will not bring any claim, arbitration,
                lawsuit or litigation in any other court or tribunal.
              </li>
              <li translate="PROFILE_TERMS5" className="ng-scope">
                I have read and understand this Acknowledgement and Agreement.
              </li>
            </ol>

            <div
              ref={agreementRef}
              className={`agreement-checkbox ${termsError ? "terms-error" : ""}`}
            >
              <input
                type="checkbox"
                id="conditions"
                checked={termsChecked}
                onChange={handleTermsCheck}
              />
              <label
                htmlFor="conditions"
                translate="ENTER_EVENT_DRUG_AGREE_LABEL"
                className="ng-scope"
              >
                I agree to the above Terms & Conditions.
              </label>
            </div>

            {termsError ? (
              <p className="agreement-error">
                <i className="fa-solid fa-circle-exclamation"></i>
                Please accept the Terms &amp; Conditions to continue.
              </p>
            ) : null}
          </div>
        </div>
      </div>}
      <div className="card font-xsss shadow-xss w-100 border-0 p-4 mb-3">
        <div className="card-body align-items-center p-0 relative">
          <i className="fa-solid fa-xmark cross-icon" onClick={() => navigate(-1)}></i>
          <h2>{isUpdateCard ? 'Enter new card' : 'Payment Details'}</h2>
          
          {isUpdateCard || isIndia ? null : <h3 className="mt-3 mb-3">
            Amount {currencySign(subscriptionData?.plan?.sign)}{" "}
            {subscriptionData?.plan?.amount}
          </h3>}
          {/* {subscriptionData?.plan?.subscription_payment ? (
            isUpdateCard ? null : <h2 className="mb-4 text-red">This membership is set to auto-renew in 365 days. You can cancel the auto-renew anytime.</h2>
          ) : null} */}

          {isIndia && !isUpdateCard ? <div className="india-payment-panel">
            <div className="india-payment-panel__head">
              <span className="india-payment-badge">One-time payment</span>
              <h4>Secure checkout with Razorpay</h4>

              <div className="india-payment-methods">
                <span><i className="fa-solid fa-mobile-screen"></i>UPI</span>
                <span><i className="fa-regular fa-credit-card"></i>Cards</span>
                <span><i className="fa-solid fa-building-columns"></i>Netbanking</span>
                <span><i className="fa-solid fa-wallet"></i>Wallets</span>
              </div>
            </div>

            <div className="india-payment-amount">
              <span className="india-payment-amount__label">
                Premium Membership
                <small>12 Months</small>
              </span>
              <span className="india-payment-amount__value">
                {currencySign(subscriptionData?.plan?.sign)} {formatAmount(subscriptionData?.plan?.amount)}
                <small>(Inclusive of GST)</small>
              </span>
            </div>

            <ul className="india-payment-points">
              <li>
                <i className="fa-solid fa-circle-check"></i>
                Valid for 365 days from payment.
              </li>
              <li>
                <i className="fa-solid fa-circle-check"></i>
                Membership activates as soon as payment is confirmed.
              </li>
            </ul>

            <div ref={billingRef}>
              <RazorpayBillingForm
                billing={billing}
                errors={billingErrors}
                showErrors={billingSubmitted}
                onChange={handleBillingChange}
              />
            </div>

            <button
              type="button"
              onClick={handleRazorpaySubscription}
              disabled={razorpayLoading}
              className="india-razorpay-btn border-0 text-white text-center fw-700"
            >
              {razorpayLoading ? "Please wait..." : `Pay ${currencySign(subscriptionData?.plan?.sign) || ""} ${formatAmount(subscriptionData?.plan?.amount)}`}
            </button>

            <p className="india-payment-secure">
              <i className="fa-solid fa-lock"></i>Payments are securely processed by Razorpay. ICN does not store your card details.
            </p>
          </div> :
            <>
              <img className="stripe-image" src="./images/stripe.png" alt="Stripe" />
              <PaymentForm
                onTermsRequired={requireTermsAgreement}
                termsChecked={termsChecked}
                goToSuccessPage={goToSuccessPage}
              />
            </>
          }

        </div>
      </div >
    </>
  );
};

export default Subscription;
